import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from "@mui/lab";
import {
  Box,
  Pagination,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import {
  getLocationStockLogsThunk,
  locationStockLogSelector,
} from "store/reducers/inventory/stock/locationStockSlice";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import ReplayIcon from "@mui/icons-material/Replay";
import AutoCompleteList from "components/AutoCompleteList";
import {
  DesktopDateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  getLocationsThunk,
  locationSelector,
} from "store/reducers/inventory/locationSlice";
import { Link, useSearchParams } from "react-router-dom";

const LocationStockLogs = () => {
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const location = searchParams.get("location");
  const pageCount = useSelector((state) => state.locationStock.logPageCount);
  const logs = useSelector(locationStockLogSelector.selectAll);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const locations = useSelector(locationSelector.selectAll);
  useEffect(() => {
    dispatch(getLocationsThunk());
  }, []);

  const [filter, setFilter] = useState({
    from_date: null,
    to_date: null,
    location: null,
  });

  const getLocationStockLogs = useCallback(() => {
    dispatch(
      getLocationStockLogsThunk({
        page,
        from_date: filter.from_date ?? "",
        to_date: filter.to_date ?? "",
        location: filter.location?.id ?? "",
      })
    );
  }, [filter.from_date, filter.location?.id, filter.to_date, page]);

  useEffect(() => {
    getLocationStockLogs();
  }, [getLocationStockLogs, page]);

  useEffect(() => {
    if (location && locations) {
      const dist = locations.find((item) => item.id === parseInt(location));
      if (dist) {
        setFilter((state) => ({ ...state, location: dist }));
      }
    }
  }, [location, locations]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };
  const getTimeDot = (direction) => {
    switch (direction) {
      case "in":
        return (
          <TimelineDot variant="filled" color="success">
            <SouthIcon fontSize="small" />
          </TimelineDot>
        );
      case "out":
        return (
          <TimelineDot variant="filled" color="info">
            <NorthIcon fontSize="small" />
          </TimelineDot>
        );
      default:
        return (
          <TimelineDot variant="filled" color="warning">
            <ReplayIcon fontSize="small" />
          </TimelineDot>
        );
    }
  };
  return (
    <Box padding={2}>
      <Stack
        direction={isSmallScreen ? "column" : "row"}
        justifyContent="center"
        spacing={1.5}
      >
        <AutoCompleteList
          items={locations}
          name="name"
          label="Filter kitchen"
          setValue={(value) => setFilter({ ...filter, location: value })}
          value={filter.location}
        />
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DesktopDateTimePicker
            label="From date"
            value={filter.from_date}
            onChange={(value) =>
              // setFromDate(value)
              setFilter({ ...filter, from_date: value })
            }
            renderInput={(params) => (
              <TextField variant="outlined" fullWidth {...params} />
            )}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DesktopDateTimePicker
            label="To date"
            value={filter.to_date}
            onChange={(value) => setFilter({ ...filter, to_date: value })}
            renderInput={(params) => (
              <TextField variant="outlined" fullWidth {...params} />
            )}
          />
        </LocalizationProvider>
      </Stack>
      <Timeline position="right">
        {logs.map((log) => (
          <TimelineItem key={log.id}>
            <TimelineOppositeContent color="text.secondary">
              <Typography
                color="text.secondary"
                component={Link}
                to={`/location-stock/history?time=${moment(
                  log.date
                ).toISOString()}&location=${
                  filter.location ? filter.location.id : ""
                }`}
                sx={{ textDecoration: "none" }}
              >
                {" "}
                {moment(log.date).format("MMMM Do YYYY, h:mm:ss a")}{" "}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              {getTimeDot(log.in_or_out)}
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Stack direction="column">
                <Typography variant="h6" component="span">
                  {log.name}
                </Typography>
                <Typography variant="caption">
                  {log.qty} Nos, {log.price} Rs per item
                </Typography>
                {log.in_or_out === "out" && log.machine_name && (
                  <Typography variant="subtitle1" component="span">
                    [Delivered to {log.machine_name}]
                  </Typography>
                )}

                {log.in_or_out === "out" && log.filling_person_name && (
                  <Typography variant="subtitle1" component="span">
                    [Delivered to {log.filling_person_name}]
                  </Typography>
                )}
                {log.in_or_out === "in" && log.filling_person_name && (
                  <Typography variant="subtitle1" component="span">
                    [Returned from {log.filling_person_name}]
                  </Typography>
                )}
                {log.in_or_out === "in" && log.machine_name && (
                  <Typography variant="subtitle1" component="span">
                    [Returned from {log.machine_name}]
                  </Typography>
                )}
                {log.in_or_out === "in" && log.distributor_name && (
                  <Typography variant="subtitle1" component="span">
                    [Received from {log.distributor_name}]
                  </Typography>
                )}
                {log.in_or_out === "ret" && (
                  <Typography variant="subtitle1" component="span">
                    [Returned to {log.distributor_name}]
                  </Typography>
                )}
              </Stack>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      <Stack direction="row" justifyContent="center">
        <Pagination
          count={pageCount}
          variant="outlined"
          shape="rounded"
          onChange={handlePageChange}
        />
      </Stack>
    </Box>
  );
};

export default LocationStockLogs;
